/**
 * Admin Portal: Channel control — show/hide channels on the TV Listings page.
 * Admin-only. Data comes from listing_channel_display (populated by listings sync).
 */
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Navigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import TvListingImage from '@/components/TvListingImage'
import { ADMIN_PORTAL_PATH } from '@/lib/adminPortal'

const CHANNEL_LIST_QUERY_KEY = 'listing-channel-display'

interface ListingChannelDisplay {
  id: string
  channel_id: string
  name: string
  icon_url: string | null
  is_displayed: boolean
  created_at: string
  updated_at: string
}

export default function AdminChannelControlPage() {
  const { user } = useAuth()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()

  const { data: channelList = [], isLoading: channelListLoading, error: channelListError } = useQuery({
    queryKey: [CHANNEL_LIST_QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('listing_channel_display')
        .select('id, channel_id, name, icon_url, is_displayed, created_at, updated_at')
        .order('name')
      if (error) throw error
      return (data || []) as ListingChannelDisplay[]
    },
    enabled: !!user && isAdmin,
  })

  const updateChannelDisplayMutation = useMutation({
    mutationFn: async ({ id, is_displayed }: { id: string; is_displayed: boolean }) => {
      const { error } = await supabase
        .from('listing_channel_display')
        .update({ is_displayed })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHANNEL_LIST_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['tv-data'] })
    },
  })

  const bulkUpdateChannelDisplayMutation = useMutation({
    mutationFn: async ({ is_displayed, ids }: { is_displayed: boolean; ids: string[] }) => {
      const BATCH = 100
      for (let i = 0; i < ids.length; i += BATCH) {
        const chunk = ids.slice(i, i + BATCH)
        const { error } = await supabase
          .from('listing_channel_display')
          .update({ is_displayed })
          .in('id', chunk)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHANNEL_LIST_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['tv-data'] })
    },
  })

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1
        className="text-3xl font-bold mb-6"
        style={{ color: 'var(--text-primary)' }}
      >
        Channel control
      </h1>

      <section
        className="rounded-lg p-6 border-2"
        style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-secondary)' }}
        aria-labelledby="channel-control-heading"
      >
        <h2 id="channel-control-heading" className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Show or hide channels on TV Listings
        </h2>
        <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Channels that have appeared in the TV listings file. Toggle whether each channel is shown or hidden on the TV Listings page.
        </p>
        {channelListLoading ? (
          <p style={{ color: 'var(--text-secondary)' }}>Loading channels…</p>
        ) : channelListError ? (
          <p style={{ color: 'var(--color-error)' }} role="alert">
            {channelListError instanceof Error ? channelListError.message : 'Failed to load channel list.'}
          </p>
        ) : channelList.length === 0 ? (
          <div>
            <p style={{ color: 'var(--text-primary)' }}>
              No channels recorded yet. Run a sync on{' '}
              <Link
                to={`${ADMIN_PORTAL_PATH}/listings-management`}
                className="underline"
                style={{ color: 'var(--color-focus)' }}
              >
                Listings control
              </Link>{' '}
              (use &quot;Sync now&quot; with &quot;Force full sync&quot; if needed) to populate the list.
            </p>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => bulkUpdateChannelDisplayMutation.mutate({ is_displayed: true, ids: channelList.map((c) => c.id) })}
                disabled={bulkUpdateChannelDisplayMutation.isPending || updateChannelDisplayMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium rounded border"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                }}
              >
                {bulkUpdateChannelDisplayMutation.isPending ? 'Updating…' : 'Select all'}
              </button>
              <button
                type="button"
                onClick={() => bulkUpdateChannelDisplayMutation.mutate({ is_displayed: false, ids: channelList.map((c) => c.id) })}
                disabled={bulkUpdateChannelDisplayMutation.isPending || updateChannelDisplayMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium rounded border"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                }}
              >
                {bulkUpdateChannelDisplayMutation.isPending ? 'Updating…' : 'Deselect all'}
              </button>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Set all channels to shown or hidden on the TV Listings page.
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ color: 'var(--text-primary)' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                    <th className="text-left py-2 pr-4">Channel</th>
                    <th className="text-left py-2 pr-4" style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>ID</th>
                    <th className="text-left py-2 pr-4">Show on TV Listings</th>
                  </tr>
                </thead>
                <tbody>
                  {channelList.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <TvListingImage
                            src={row.icon_url}
                            alt=""
                            width={24}
                            height={24}
                            objectFit="contain"
                          />
                          <span>{row.name}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs" style={{ color: 'var(--text-secondary)' }} title={row.channel_id}>
                        {row.channel_id.length > 24 ? `${row.channel_id.slice(0, 21)}…` : row.channel_id}
                      </td>
                      <td className="py-2 pr-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={row.is_displayed}
                            onChange={(e) => updateChannelDisplayMutation.mutate({ id: row.id, is_displayed: e.target.checked })}
                            disabled={updateChannelDisplayMutation.isPending || bulkUpdateChannelDisplayMutation.isPending}
                            className="rounded"
                            aria-label={`Show ${row.name} on TV Listings`}
                          />
                          <span style={{ color: 'var(--text-secondary)' }}>{row.is_displayed ? 'Shown' : 'Hidden'}</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
